import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Download, Upload, Trash2, Save, Pencil } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BannersTab from "../components/admin/BannersTab";
import BroadcastTab from "../components/admin/BroadcastTab";
import HomepageTab from "../components/admin/HomepageTab";
import InquiriesTab from "../components/admin/InquiriesTab";
import ApprovalsTab from "../components/admin/ApprovalsTab";
import ReferralsTab from "../components/admin/ReferralsTab";
import TeachersTab from "../components/admin/TeachersTab";
import PackagesTab from "../components/admin/PackagesTab";
import { LogoSettings } from "../components/admin/LogoSettings";
import { AdminShell } from "../components/admin/AdminShell";
import { AdminOverview } from "../components/admin/AdminOverview";
import { EditModal } from "../components/admin/EditModal";
import { uploadImage } from "../lib/upload";

const INST_FIELDS = [
  { key: "name", label: "Name" }, { key: "type", label: "Type", type: "select", options: ["coaching", "school", "college"] },
  { key: "city", label: "City" }, { key: "area", label: "Area" }, { key: "phone", label: "Phone" }, { key: "whatsapp", label: "WhatsApp" },
  { key: "email", label: "Email (private)" }, { key: "since_year", label: "Since year" }, { key: "admissions", label: "Admissions", type: "number" }, { key: "banner_url", label: "Banner URL" },
  { key: "exams", label: "Exams", type: "tags", span: 2 }, { key: "description", label: "Description", type: "textarea" },
];
const STUDENT_FIELDS = [
  { key: "name", label: "Name" }, { key: "email", label: "Email (login ID)" }, { key: "phone", label: "Phone" }, { key: "whatsapp", label: "WhatsApp" },
  { key: "city", label: "City" }, { key: "future_goal", label: "Future goal" }, { key: "points", label: "Points", type: "number" }, { key: "blocked", label: "Blocked", type: "checkbox" },
];

const TABS = ["Overview", "Approvals", "Institutions", "Inquiries", "Students", "Listed Students", "Listed Teachers", "Teachers", "Teacher Reviews", "Reviews", "Points", "Referrals", "Packages", "Homepage", "Banners", "Broadcast", "Locations", "Staff", "Site Settings", "Integrations", "Data (Excel)", "Security"];
const TAB_PERMS = {
  Approvals: "reviews", Institutions: "institutions", Inquiries: "institutions", Students: "students", "Listed Students": "students", "Listed Teachers": "students",
  Referrals: "payments", Teachers: "students", "Teacher Reviews": "reviews",
  Reviews: "reviews", Points: "points", Packages: "payments", Homepage: "settings", Banners: "settings", Broadcast: "settings", Locations: "locations",
  "Site Settings": "settings", Integrations: "settings", "Data (Excel)": "data",
};
const ALL_PERMS = ["institutions", "students", "reviews", "payments", "points", "settings", "locations", "data"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [students, setStudents] = useState([]);
  const [listedStudents, setListedStudents] = useState([]);
  const [listedTeachers, setListedTeachers] = useState([]);
  const [reviewsList, setReviewsList] = useState([]);
  const [locations, setLocations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "", permissions: [] });
  const [newCity, setNewCity] = useState("");
  const [newArea, setNewArea] = useState({ city: "", area: "" });
  const [searchInst, setSearchInst] = useState("");
  const [searchStudent, setSearchStudent] = useState("");
  const [searchReview, setSearchReview] = useState("");
  const fileReviews = useRef();
  const [points, setPoints] = useState(null);
  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [editing, setEditing] = useState(null);
  const fileStudent = useRef();
  const fileInst = useRef();

  const hasPerm = (t) => {
    const p = TAB_PERMS[t];
    if (t === "Staff") return !!user?.is_super;
    if (!p) return true;
    return user?.is_super || (user?.permissions || []).includes(p);
  };
  const visibleTabs = TABS.filter(hasPerm);

  const can = (p) => user?.is_super || (user?.permissions || []).includes(p);

  const load = useCallback(() => {
    api.get("/admin/dashboard").then((r) => setStats(r.data.cards)).catch(() => {});
    if (can("institutions")) api.get("/admin/institutions").then((r) => setInstitutions(r.data)).catch(() => {});
    if (can("students")) {
      api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => {});
      api.get("/admin/listed-students").then((r) => setListedStudents(r.data)).catch(() => {});
      api.get("/admin/listed-teachers").then((r) => setListedTeachers(r.data)).catch(() => {});
    }
    if (can("reviews")) api.get("/admin/reviews").then((r) => setReviewsList(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data.cities || [])).catch(() => {});
    if (user?.is_super) api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
    if (can("points")) api.get("/admin/points").then((r) => setPoints(r.data)).catch(() => {});
    if (can("payments") || can("institutions")) api.get("/admin/packages").then((r) => setPackages(r.data)).catch(() => {});
    if (can("settings")) {
      api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
      api.get("/admin/integrations").then((r) => setIntegrations(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const matches = (fields, q) => !q || fields.join(" ").toLowerCase().includes(q.toLowerCase());
  const filteredInstitutions = useMemo(() => institutions.filter((i) => matches([i.name, i.city, i.area, i.type, i.email], searchInst)), [institutions, searchInst]);
  const filteredStudents = useMemo(() => students.filter((s) => matches([s.name, s.email, s.phone, s.mobile, s.city, s.future_goal], searchStudent)), [students, searchStudent]);
  const filteredReviews = useMemo(() => reviewsList.filter((r) => matches([r.student_name, r.institution_name, r.comment, r.student_phone], searchReview)), [reviewsList, searchReview]);

  const act = async (fn) => {
    try { const res = await fn(); toast.success(res.data.message); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const confirmAct = (msg, fn) => {
    if (window.confirm(msg)) act(fn);
  };

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  const upload = async (path, file) => {
    const fd = new FormData();
    fd.append("file", file);
    act(() => api.post(path, fd));
  };

  const pendingBadges = { Teachers: stats?.pending_teachers, Approvals: stats?.pending_reviews, Referrals: stats?.pending_withdrawals };
  return (
    <AdminShell tabs={visibleTabs} tab={tab} setTab={setTab} badges={pendingBadges} settings={settings}>
      {editing && <EditModal title={editing.title} fields={editing.fields} initial={editing.data} testid={editing.testid} onClose={() => setEditing(null)}
        onSave={async (f) => { try { const r = await api.put(editing.path, f); toast.success(r.data.message); load(); } catch (err) { toast.error(apiError(err)); throw err; } }} />}

      {tab === "Overview" && <AdminOverview go={setTab} />}

      {tab === "Institutions" && (
        <div className="mt-8">
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <input data-testid="admin-inst-search" placeholder="Search name, city, area, type..." value={searchInst}
              onChange={(e) => setSearchInst(e.target.value)} className="nb-input" style={{ width: 320 }} />
            <button data-testid="inst-tab-export-btn" onClick={() => download("/admin/export/institutions", "institutions.xlsx")} className="nb-btn nb-btn-sm">
              <Download className="w-4 h-4" /> Export Excel
            </button>
            <button data-testid="inst-tab-import-btn" onClick={() => fileInst.current.click()} className="nb-btn nb-btn-lime nb-btn-sm">
              <Upload className="w-4 h-4" /> Bulk Upload
            </button>
            <span className="text-xs text-slate-500">Bulk upload updates existing institutes by Name (incl. Email & Password columns) or creates new ones.</span>
          </div>
          <div className="nb overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b-2 border-[#171B33] text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="p-4">Name</th><th className="p-4">Type</th><th className="p-4">City / Area</th><th className="p-4">Score</th><th className="p-4">Badge</th><th className="p-4">Actions</th>
            </tr></thead>
            <tbody>
              {filteredInstitutions.map((i) => (
                <tr key={i.id} data-testid={`admin-inst-row-${i.id}`} className="border-b border-slate-200 last:border-0">
                  <td className="p-4 font-bold">{i.name}</td>
                  <td className="p-4 capitalize">{i.type}</td>
                  <td className="p-4">{i.city} / {i.area}</td>
                  <td className="p-4 font-display font-bold">{Math.round(i.total_score)}</td>
                  <td className="p-4">
                    <select data-testid={`admin-badge-select-${i.id}`} value={i.badge || ""}
                      onChange={(e) => act(() => api.put(`/admin/institutions/${i.id}/badge`, { badge: e.target.value || null }))}
                      className="nb-input" style={{ padding: "4px 10px", fontSize: 12, width: "auto" }}>
                      <option value="">None</option>
                      <option value="featured">Featured</option>
                      <option value="verified">Verified</option>
                    </select>
                    <select data-testid={`admin-plan-select-${i.id}`} value={i.package_id || ""}
                      onChange={(e) => act(() => api.put(`/admin/institutions/${i.id}/plan`, { package_id: e.target.value || null }))}
                      className="nb-input mt-1" style={{ padding: "4px 10px", fontSize: 12, width: "auto" }}>
                      <option value="">Free plan</option>
                      {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td className="p-4">
                    <span className="flex items-center gap-3">
                      <button data-testid={`admin-edit-inst-${i.id}`} onClick={() => setEditing({ title: `Edit ${i.name}`, fields: INST_FIELDS, data: i, path: `/admin/institutions/${i.id}`, testid: "edit-inst" })}
                        className="nb-badge" style={{ background: "#A3E635", cursor: "pointer" }}><Pencil className="w-3 h-3" /> Edit</button>
                      <button data-testid={`admin-block-inst-${i.id}`}
                        onClick={() => act(() => api.put(`/admin/institutions/${i.id}/block`))}
                        className="nb-badge" style={{ background: i.blocked ? "#FCA5A5" : "#fff", cursor: "pointer" }}>
                        {i.blocked ? "Unblock" : "Block"}
                      </button>
                      <button data-testid={`admin-delete-inst-${i.id}`}
                        onClick={() => confirmAct(`Delete "${i.name}" and its owner account? This cannot be undone.`, () => api.delete(`/admin/institutions/${i.id}`))}
                        className="text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "Students" && (
        <div className="mt-8">
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <input data-testid="admin-student-search" placeholder="Search name, email, phone, city, goal..." value={searchStudent}
              onChange={(e) => setSearchStudent(e.target.value)} className="nb-input" style={{ width: 320 }} />
            <button data-testid="student-tab-export-btn" onClick={() => download("/admin/export/students", "students.xlsx")} className="nb-btn nb-btn-sm">
              <Download className="w-4 h-4" /> Download Excel
            </button>
            <button data-testid="student-tab-import-btn" onClick={() => fileStudent.current.click()} className="nb-btn nb-btn-lime nb-btn-sm">
              <Upload className="w-4 h-4" /> Bulk Upload
            </button>
            <span className="text-xs text-slate-500">Upload updates existing students by Email (supports Password & FutureGoal columns).</span>
          </div>
          <div className="nb overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b-2 border-[#171B33] text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="p-4">Name</th><th className="p-4">Email (Login ID)</th><th className="p-4">Phone</th><th className="p-4">WhatsApp</th><th className="p-4">City</th><th className="p-4">Future Goal</th><th className="p-4">Points</th><th className="p-4">Actions</th>
            </tr></thead>
            <tbody>
              {filteredStudents.map((s) => (
                <tr key={s.id} data-testid={`admin-student-row-${s.id}`} className="border-b border-slate-200 last:border-0">
                  <td className="p-4 font-bold">{s.name}</td>
                  <td className="p-4">{s.email || "—"}</td>
                  <td className="p-4 font-display">{s.phone || s.mobile || "—"}</td>
                  <td className="p-4 font-display">{s.whatsapp || "—"}</td>
                  <td className="p-4">{s.city}</td>
                  <td className="p-4">{s.future_goal || "—"}</td>
                  <td className="p-4 font-display font-bold">{s.points || 0}</td>
                  <td className="p-4">
                    <span className="flex items-center gap-3">
                      <button data-testid={`admin-edit-student-${s.id}`} onClick={() => setEditing({ title: `Edit ${s.name}`, fields: STUDENT_FIELDS, data: s, path: `/admin/users/${s.id}`, testid: "edit-student" })}
                        className="nb-badge" style={{ background: "#A3E635", cursor: "pointer" }}><Pencil className="w-3 h-3" /> Edit</button>
                      <button data-testid={`admin-block-student-${s.id}`}
                        onClick={() => act(() => api.put(`/admin/users/${s.id}/block`))}
                        className="nb-badge" style={{ background: s.blocked ? "#FCA5A5" : "#fff", cursor: "pointer" }}>
                        {s.blocked ? "Unblock" : "Block"}
                      </button>
                      <button data-testid={`admin-delete-student-${s.id}`}
                        onClick={() => confirmAct(`Delete student "${s.name}" and their reviews?`, () => api.delete(`/admin/students/${s.id}`))}
                        className="text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "Listed Students" && (
        <div className="mt-8">
          <p className="text-sm text-slate-600 mb-4">Students added by institutes & schools. Verify the phone number to award points to the institution.</p>
          <div className="nb overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b-2 border-[#171B33] text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="p-4">Institution</th><th className="p-4">Student</th><th className="p-4">Phone</th><th className="p-4">Batch / Class</th><th className="p-4">Status</th><th className="p-4">Action</th>
              </tr></thead>
              <tbody>
                {listedStudents.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-500" data-testid="listed-students-empty">No students listed yet.</td></tr>
                )}
                {listedStudents.map((s) => (
                  <tr key={s.id} data-testid={`listed-student-row-${s.id}`} className="border-b border-slate-200 last:border-0">
                    <td className="p-4 font-bold">{s.institution_name} <span className="text-xs text-slate-400 capitalize font-normal">({s.institution_type})</span></td>
                    <td className="p-4 font-semibold">{s.name}</td>
                    <td className="p-4 font-display">{s.phone || "—"}</td>
                    <td className="p-4">{s.batch || "—"}</td>
                    <td className="p-4">
                      {s.verified
                        ? <span className="nb-badge" style={{ background: "#A3E635" }}>Verified</span>
                        : <span className="nb-badge" style={{ background: "#FCD34D" }}>Pending</span>}
                    </td>
                    <td className="p-4">
                      {!s.verified && (
                        <button data-testid={`verify-listed-student-${s.id}`}
                          onClick={() => act(() => api.put(`/admin/listed-students/${s.id}/verify`))}
                          className="nb-btn nb-btn-lime nb-btn-sm">Verify & Award Points</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "Listed Teachers" && (
        <div className="mt-8">
          <p className="text-sm text-slate-600 mb-4">Teachers added by institutes & schools. Phone numbers are visible only to admins. Verify to award points.</p>
          <div className="nb overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b-2 border-[#171B33] text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="p-4">Institution</th><th className="p-4">Teacher</th><th className="p-4">Phone (admin-only)</th><th className="p-4">Subject</th><th className="p-4">Status</th><th className="p-4">Action</th>
              </tr></thead>
              <tbody>
                {listedTeachers.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No teachers listed yet.</td></tr>}
                {listedTeachers.map((t) => (
                  <tr key={t.id} data-testid={`listed-teacher-row-${t.id}`} className="border-b border-slate-200 last:border-0">
                    <td className="p-4 font-bold">{t.institution_name}</td>
                    <td className="p-4 font-semibold">{t.name}</td>
                    <td className="p-4 font-display">{t.phone || "—"}</td>
                    <td className="p-4">{t.subject || "—"}</td>
                    <td className="p-4">{t.verified
                      ? <span className="nb-badge" style={{ background: "#A3E635" }}>Verified</span>
                      : <span className="nb-badge" style={{ background: "#FCD34D" }}>Pending</span>}</td>
                    <td className="p-4">
                      {!t.verified && (
                        <button data-testid={`verify-listed-teacher-${t.id}`}
                          onClick={() => act(() => api.put(`/admin/listed-teachers/${t.id}/verify`))}
                          className="nb-btn nb-btn-lime nb-btn-sm">Verify & Award Points</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "Reviews" && (
        <div className="mt-8">
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <input data-testid="admin-review-search" placeholder="Search student, institution, comment..." value={searchReview}
              onChange={(e) => setSearchReview(e.target.value)} className="nb-input" style={{ width: 320 }} />
            <button data-testid="review-export-btn" onClick={() => download("/admin/export/reviews", "reviews.xlsx")} className="nb-btn nb-btn-sm">
              <Download className="w-4 h-4" /> Download Excel
            </button>
            <button data-testid="review-import-btn" onClick={() => fileReviews.current.click()} className="nb-btn nb-btn-lime nb-btn-sm">
              <Upload className="w-4 h-4" /> Bulk Upload
            </button>
            <input type="file" accept=".xlsx,.xls" hidden ref={fileReviews}
              onChange={(e) => { if (e.target.files[0]) upload("/admin/import/reviews", e.target.files[0]); e.target.value = ""; }} />
            <span className="text-xs text-slate-500">Call the student to verify, then mark as genuine.</span>
          </div>
          <div className="space-y-4">
            {reviewsList.length === 0 && <p className="text-sm text-slate-500">No reviews yet.</p>}
            {filteredReviews.map((r) => (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="nb p-5 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-bold text-sm flex items-center gap-2 flex-wrap">
                    {r.student_name} → <span className="font-display">{r.institution_name}</span>
                    {r.student_phone && <a href={`tel:${r.student_phone}`} data-testid={`review-phone-${r.id}`} className="nb-badge" style={{ background: "#93C5FD" }}>📞 {r.student_phone}</a>}
                    {r.genuine && <span data-testid={`review-genuine-${r.id}`} className="nb-badge" style={{ background: "#A3E635" }}>✓ Genuine Review</span>}
                  </p>
                  <div className="flex gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                    <span>Teachers: {r.teacher_quality}/5</span>
                    <span>Institute: {r.institute_quality ?? "—"}/5</span>
                    <span>Style: {r.teaching_style ?? "—"}/5</span>
                    <span>Environment: {r.environment ?? "—"}/5</span>
                  </div>
                  {r.comment && <p className="text-sm text-slate-600 mt-2">{r.comment}</p>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <button data-testid={`admin-verify-review-${r.id}`}
                    onClick={() => act(() => api.put(`/admin/reviews/${r.id}/verify`))}
                    className="nb-badge" style={{ background: r.genuine ? "#FCA5A5" : "#A3E635", cursor: "pointer" }}>
                    {r.genuine ? "Remove Genuine" : "Mark Genuine"}
                  </button>
                  <button data-testid={`admin-delete-review-${r.id}`}
                    onClick={() => confirmAct("Delete this review? Rankings will recalculate.", () => api.delete(`/admin/reviews/${r.id}`))}
                    className="text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "Locations" && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl">
          <div className="nb p-6">
            <h3 className="font-display font-extrabold mb-4">Add City / Area</h3>
            <div className="flex gap-2">
              <input data-testid="admin-new-city-input" placeholder="New city name" value={newCity}
                onChange={(e) => setNewCity(e.target.value)} className="nb-input flex-1" />
              <button data-testid="admin-add-city-btn" onClick={() => { act(() => api.post("/admin/locations/city", { city: newCity })); setNewCity(""); }}
                className="nb-btn nb-btn-lime nb-btn-sm">Add City</button>
            </div>
            <div className="flex gap-2 mt-4">
              <select data-testid="admin-area-city-select" value={newArea.city} onChange={(e) => setNewArea({ ...newArea, city: e.target.value })} className="nb-input" style={{ width: "40%" }}>
                <option value="">City...</option>
                {locations.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
              <input data-testid="admin-new-area-input" placeholder="New area name" value={newArea.area}
                onChange={(e) => setNewArea({ ...newArea, area: e.target.value })} className="nb-input flex-1" />
              <button data-testid="admin-add-area-btn" onClick={() => { act(() => api.post("/admin/locations/area", newArea)); setNewArea({ city: newArea.city, area: "" }); }}
                className="nb-btn nb-btn-lime nb-btn-sm">Add Area</button>
            </div>
          </div>
          <div className="space-y-4">
            {locations.map((c) => (
              <div key={c.name} data-testid={`admin-city-${c.name}`} className="nb p-5">
                <div className="flex items-center justify-between">
                  <p className="font-display font-extrabold">{c.name}</p>
                  <button data-testid={`admin-delete-city-${c.name}`}
                    onClick={() => confirmAct(`Remove city "${c.name}" and its areas from the managed list?`, () => api.delete(`/admin/locations/city/${encodeURIComponent(c.name)}`))}
                    className="text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {(c.areas || []).map((a) => (
                    <span key={a} className="nb-badge">{a}
                      <button onClick={() => act(() => api.delete(`/admin/locations/area/${encodeURIComponent(c.name)}/${encodeURIComponent(a)}`))}
                        className="ml-1 text-red-500 font-bold">×</button>
                    </span>
                  ))}
                  {(c.areas || []).length === 0 && <span className="text-xs text-slate-500">No areas yet</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "Staff" && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="nb p-6">
            <h3 className="font-display font-extrabold mb-1">Create Staff Admin</h3>
            <p className="text-xs text-slate-500 mb-4">Assign specific responsibilities — staff can only access the panels you tick.</p>
            <div className="space-y-3">
              <div><label className="nb-label">Name</label>
                <input data-testid="staff-name-input" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} className="nb-input mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="nb-label">Email</label>
                  <input data-testid="staff-email-input" type="email" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} className="nb-input mt-1.5" /></div>
                <div><label className="nb-label">Password</label>
                  <input data-testid="staff-password-input" value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} className="nb-input mt-1.5" /></div>
              </div>
              <div>
                <label className="nb-label">Permissions</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ALL_PERMS.map((p) => {
                    const on = newStaff.permissions.includes(p);
                    return (
                      <button type="button" key={p} data-testid={`staff-perm-${p}`}
                        onClick={() => setNewStaff({ ...newStaff, permissions: on ? newStaff.permissions.filter((x) => x !== p) : [...newStaff.permissions, p] })}
                        className="nb-badge" style={{ background: on ? "#A3E635" : "#fff", cursor: "pointer" }}>{p}</button>
                    );
                  })}
                </div>
              </div>
              <button data-testid="staff-create-btn"
                onClick={() => { act(() => api.post("/admin/staff", newStaff)); setNewStaff({ name: "", email: "", password: "", permissions: [] }); }}
                className="nb-btn nb-btn-lime">Create Staff Admin</button>
            </div>
          </div>
          <div className="space-y-4">
            {staff.length === 0 && <p className="text-sm text-slate-500">No staff admins yet.</p>}
            {staff.map((s) => (
              <div key={s.id} data-testid={`staff-row-${s.id}`} className="nb p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display font-extrabold">{s.name}</p>
                    <p className="text-xs text-slate-500">{s.email}</p>
                  </div>
                  <button data-testid={`staff-delete-${s.id}`}
                    onClick={() => confirmAct(`Remove staff admin "${s.name}"?`, () => api.delete(`/admin/staff/${s.id}`))}
                    className="text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {(s.permissions || []).map((p) => <span key={p} className="nb-badge" style={{ background: "#F2FBE3" }}>{p}</span>)}
                  {(s.permissions || []).length === 0 && <span className="text-xs text-slate-500">No permissions</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "Points" && points && (
        <div className="mt-8 nb p-8 max-w-2xl">
          <h2 className="font-display text-xl font-extrabold mb-6">Point Values & Limits</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(points).map(([k, v]) => (
              <div key={k}>
                <label className="nb-label">{k.replace(/_/g, " ")}</label>
                <input data-testid={`points-input-${k}`} type="number" value={v}
                  onChange={(e) => setPoints({ ...points, [k]: e.target.value })} className="nb-input mt-1.5" />
              </div>
            ))}
          </div>
          <button data-testid="points-save-btn" onClick={() => act(() => api.put("/admin/points", points))} className="nb-btn nb-btn-lime mt-6">
            <Save className="w-4 h-4" /> Save Point Values
          </button>
        </div>
      )}

      {tab === "Packages" && <PackagesTab packages={packages} reload={() => api.get("/admin/packages").then((r) => setPackages(r.data)).catch(() => {})} />}
      {tab === "Referrals" && <ReferralsTab />}
      {tab === "Teachers" && <TeachersTab />}
      {tab === "Teacher Reviews" && <TeachersTab reviewsOnly />}
      {tab === "Approvals" && <ApprovalsTab />}

      {tab === "Banners" && <BannersTab />}
      {tab === "Homepage" && <HomepageTab onBanners={() => setTab("Banners")} />}
      {tab === "Inquiries" && <InquiriesTab />}
      {tab === "Broadcast" && <BroadcastTab />}

      {tab === "Site Settings" && settings && (
        <div className="mt-8 nb p-8 max-w-2xl">
          <h2 className="font-display text-xl font-extrabold mb-6">Business & Contact Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="nb-label">Business Name</label>
              <input data-testid="settings-business-name" value={settings.business_name} onChange={(e) => setSettings({ ...settings, business_name: e.target.value })} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">Tagline</label>
              <input data-testid="settings-tagline" value={settings.tagline || ""} onChange={(e) => setSettings({ ...settings, tagline: e.target.value })} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">Phone</label>
              <input data-testid="settings-phone" value={settings.phone} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">WhatsApp</label>
              <input data-testid="settings-whatsapp" value={settings.whatsapp} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">Email</label>
              <input data-testid="settings-email" value={settings.email || ""} onChange={(e) => setSettings({ ...settings, email: e.target.value })} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">Address</label>
              <input data-testid="settings-address" value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className="nb-input mt-1.5" /></div>
            <LogoSettings settings={settings} setSettings={setSettings} />
            <div className="sm:col-span-2"><label className="nb-label">App icon (phone home screen — square PNG, 512×512)</label>
              <div className="mt-1.5 flex items-center gap-3">
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" id="app-icon-file" data-testid="settings-app-icon-file"
                  onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return;
                    try { const url = await uploadImage(f); setSettings({ ...settings, app_icon_url: url }); toast.success("Icon uploaded — click Save Settings"); }
                    catch (err) { toast.error(apiError(err)); } }} />
                <button data-testid="settings-app-icon-btn" onClick={() => document.getElementById("app-icon-file").click()} className="nb-btn nb-btn-sm"><Upload className="w-4 h-4" /> Upload icon</button>
                {settings.app_icon_url && <img src={settings.app_icon_url} alt="app icon" className="w-12 h-12 rounded-xl border-2 border-[#171B33] object-cover" />}
              </div>
            </div>
            <div className="sm:col-span-2"><label className="nb-label">Splash screen (mobile app launch — portrait PNG/JPG, 1080×1920)</label>
              <div className="mt-1.5 flex items-center gap-3">
                <input type="file" accept="image/*" className="hidden" id="splash-file" data-testid="settings-splash-file"
                  onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return;
                    try { const url = await uploadImage(f); setSettings({ ...settings, splash_mobile_url: url }); toast.success("Splash uploaded — click Save Settings"); }
                    catch (err) { toast.error(apiError(err)); } }} />
                <button data-testid="settings-splash-btn" onClick={() => document.getElementById("splash-file").click()} className="nb-btn nb-btn-sm"><Upload className="w-4 h-4" /> Upload splash</button>
                {settings.splash_mobile_url && <img src={settings.splash_mobile_url} alt="splash" className="w-9 h-16 rounded-lg border-2 border-[#171B33] object-cover" />}
                {settings.splash_mobile_url && <button data-testid="settings-splash-remove" onClick={() => setSettings({ ...settings, splash_mobile_url: "" })} className="text-xs text-slate-400 hover:text-red-600">remove</button>}
              </div>
            </div>
            {Object.entries(settings.links || {}).map(([k, v]) => (
              <div key={k}><label className="nb-label">{k} link</label>
                <input data-testid={`settings-link-${k}`} value={v} onChange={(e) => setSettings({ ...settings, links: { ...settings.links, [k]: e.target.value } })} className="nb-input mt-1.5" /></div>
            ))}
          </div>
          <button data-testid="settings-save-btn" onClick={() => act(() => api.put("/admin/settings", settings))} className="nb-btn nb-btn-lime mt-6">
            <Save className="w-4 h-4" /> Save Settings
          </button>
        </div>
      )}

      {tab === "Integrations" && integrations && (
        <div className="mt-8 space-y-6 max-w-3xl">
          <p className="text-sm text-slate-600">Enter your API keys here and save — changes take effect immediately. Saved secrets are masked; leave them masked to keep the existing value. If a service is disabled or has no keys, the platform automatically falls back to demo mode.</p>

          <div className="nb p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-extrabold">Razorpay Payments</h2>
              <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
                <input type="checkbox" data-testid="integ-razorpay-enabled" checked={integrations.razorpay_enabled}
                  onChange={(e) => setIntegrations({ ...integrations, razorpay_enabled: e.target.checked })} className="w-4 h-4 accent-lime-500" />
                Enabled
              </label>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="nb-label">Key ID</label>
                <input data-testid="integ-razorpay-key-id" value={integrations.razorpay_key_id} placeholder="rzp_test_..."
                  onChange={(e) => setIntegrations({ ...integrations, razorpay_key_id: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">Key Secret</label>
                <input data-testid="integ-razorpay-key-secret" value={integrations.razorpay_key_secret} placeholder="Key secret"
                  onChange={(e) => setIntegrations({ ...integrations, razorpay_key_secret: e.target.value })} className="nb-input mt-1.5" /></div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Get keys at dashboard.razorpay.com → Settings → API Keys. Without keys, package purchases run in demo mode.</p>
          </div>

          <div className="nb p-6" data-testid="integ-smtp-card">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-extrabold">Email SMTP — sign-up & login OTP</h2>
              <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
                <input type="checkbox" data-testid="integ-smtp-enabled" checked={!!integrations.smtp_enabled}
                  onChange={(e) => setIntegrations({ ...integrations, smtp_enabled: e.target.checked })} className="w-4 h-4 accent-lime-500" />
                Enabled
              </label>
            </div>
            <p className="text-xs text-slate-500 mt-1">Works with any provider (Gmail, Zoho, Hostinger, Outlook, SendGrid SMTP…). Port 465 uses SSL; 587 uses STARTTLS. Until enabled, OTP codes are shown on screen in test mode.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              <div className="sm:col-span-2"><label className="nb-label">SMTP host</label>
                <input data-testid="integ-smtp-host" value={integrations.smtp_host || ""} placeholder="smtp.gmail.com" onChange={(e) => setIntegrations({ ...integrations, smtp_host: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">Port</label>
                <input data-testid="integ-smtp-port" value={integrations.smtp_port || ""} placeholder="465" onChange={(e) => setIntegrations({ ...integrations, smtp_port: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">Username / email</label>
                <input data-testid="integ-smtp-user" value={integrations.smtp_user || ""} placeholder="you@yourdomain.com" onChange={(e) => setIntegrations({ ...integrations, smtp_user: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">Password / app password</label>
                <input data-testid="integ-smtp-password" type="password" value={integrations.smtp_password || ""} placeholder="••••••••" onChange={(e) => setIntegrations({ ...integrations, smtp_password: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">From address (optional)</label>
                <input data-testid="integ-smtp-from" value={integrations.smtp_from || ""} placeholder="noreply@yourdomain.com" onChange={(e) => setIntegrations({ ...integrations, smtp_from: e.target.value })} className="nb-input mt-1.5" /></div>
            </div>
            <button type="button" data-testid="integ-smtp-test" onClick={() => act(() => api.post("/admin/integrations/test-email"))} className="nb-btn nb-btn-sm mt-3">Send test email to me</button>
          </div>
          <div className="nb p-6">
              <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
                <input type="checkbox" data-testid="integ-gmail-enabled" checked={!!integrations.gmail_enabled}
                  onChange={(e) => setIntegrations({ ...integrations, gmail_enabled: e.target.checked })} className="w-4 h-4 accent-lime-500" />
                Gmail SMTP (legacy fallback — used only if the SMTP card above is disabled)
              </label>
              <p className="text-xs text-slate-500 mt-1">Google Account → Security → 2-Step Verification → App passwords. Until enabled, OTP codes are shown on screen in test mode.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <div><label className="nb-label">Gmail address</label>
                  <input data-testid="integ-gmail-user" value={integrations.gmail_user || ""} placeholder="you@gmail.com"
                    onChange={(e) => setIntegrations({ ...integrations, gmail_user: e.target.value })} className="nb-input mt-1.5" /></div>
                <div><label className="nb-label">App password (16 chars)</label>
                  <input data-testid="integ-gmail-password" value={integrations.gmail_app_password || ""} placeholder="xxxx xxxx xxxx xxxx"
                    onChange={(e) => setIntegrations({ ...integrations, gmail_app_password: e.target.value })} className="nb-input mt-1.5" /></div>
              </div>
            </div>
            <div className="nb p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-extrabold">Twilio SMS (OTP)</h2>
              <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
                <input type="checkbox" data-testid="integ-twilio-enabled" checked={integrations.twilio_enabled}
                  onChange={(e) => setIntegrations({ ...integrations, twilio_enabled: e.target.checked })} className="w-4 h-4 accent-lime-500" />
                Enabled
              </label>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><label className="nb-label">Account SID</label>
                <input data-testid="integ-twilio-sid" value={integrations.twilio_sid} placeholder="AC..."
                  onChange={(e) => setIntegrations({ ...integrations, twilio_sid: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">Auth Token</label>
                <input data-testid="integ-twilio-token" value={integrations.twilio_token} placeholder="Auth token"
                  onChange={(e) => setIntegrations({ ...integrations, twilio_token: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">From Number</label>
                <input data-testid="integ-twilio-from" value={integrations.twilio_from} placeholder="+1234567890"
                  onChange={(e) => setIntegrations({ ...integrations, twilio_from: e.target.value })} className="nb-input mt-1.5" /></div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Get credentials at console.twilio.com. Without them, the OTP is shown on screen (demo mode).</p>
          </div>

          <div className="nb p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-extrabold">Resend Email (Inquiry Alerts)</h2>
              <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
                <input type="checkbox" data-testid="integ-resend-enabled" checked={integrations.resend_enabled}
                  onChange={(e) => setIntegrations({ ...integrations, resend_enabled: e.target.checked })} className="w-4 h-4 accent-lime-500" />
                Enabled
              </label>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="nb-label">API Key</label>
                <input data-testid="integ-resend-key" value={integrations.resend_api_key} placeholder="re_..."
                  onChange={(e) => setIntegrations({ ...integrations, resend_api_key: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">From Email</label>
                <input data-testid="integ-resend-from" value={integrations.resend_from} placeholder="alerts@yourdomain.com"
                  onChange={(e) => setIntegrations({ ...integrations, resend_from: e.target.value })} className="nb-input mt-1.5" /></div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Get a key at resend.com. Institutes always get a WhatsApp reply link on each inquiry; email alerts are sent when this is enabled.</p>
          </div>

          <button data-testid="integ-save-btn" onClick={() => act(() => api.put("/admin/integrations", integrations))} className="nb-btn nb-btn-lime">
            <Save className="w-4 h-4" /> Save Integration Settings
          </button>
        </div>
      )}

      {tab === "Data (Excel)" && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          {[
            { title: "Students", exportPath: "/admin/export/students", importPath: "/admin/import/students", ref: fileStudent, file: "students.xlsx" },
            { title: "Institutions", exportPath: "/admin/export/institutions", importPath: "/admin/import/institutions", ref: fileInst, file: "institutions.xlsx" },
          ].map((d) => (
            <div key={d.title} className="nb p-6">
              <h3 className="font-display font-extrabold mb-4">{d.title}</h3>
              <div className="flex gap-3 flex-wrap">
                <button data-testid={`export-${d.title.toLowerCase()}-btn`} onClick={() => download(d.exportPath, d.file)} className="nb-btn nb-btn-sm">
                  <Download className="w-4 h-4" /> Export Excel
                </button>
                <button data-testid={`import-${d.title.toLowerCase()}-btn`} onClick={() => d.ref.current.click()} className="nb-btn nb-btn-lime nb-btn-sm">
                  <Upload className="w-4 h-4" /> Import Excel
                </button>
                <input type="file" accept=".xlsx,.xls" hidden ref={d.ref}
                  onChange={(e) => { if (e.target.files[0]) upload(d.importPath, e.target.files[0]); e.target.value = ""; }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Security" && (
        <div className="mt-8 nb p-8 max-w-md">
          <h2 className="font-display text-xl font-extrabold mb-6">Change Admin Password</h2>
          <div className="space-y-4">
            <div><label className="nb-label">Current Password</label>
              <input data-testid="pwd-current-input" type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">New Password</label>
              <input data-testid="pwd-new-input" type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="nb-input mt-1.5" /></div>
            <button data-testid="pwd-save-btn" onClick={() => { act(() => api.put("/auth/change-password", pwd)); setPwd({ current_password: "", new_password: "" }); }} className="nb-btn nb-btn-lime">
              Update Password
            </button>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
